import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'

import type { NavItem } from './navigation'

interface SidebarNavProps {
  items: NavItem[]
  /** Página atual, para abrir o grupo que a contém. */
  current?: NavItem
  /** Menu recolhido: só ícones, com o nome no tooltip. */
  collapsed?: boolean
  onNavigate?: () => void
}

const itemClass =
  'flex min-h-2 w-full items-center gap-3 rounded-md px-3 py-2 text-sm leading-tight font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'

const activeClass =
  'data-[status=active]:bg-sidebar-primary data-[status=active]:font-bold data-[status=active]:text-sidebar-primary-foreground data-[status=active]:shadow-brand'

const itemActive = 'bg-sidebar-primary font-bold text-sidebar-primary-foreground shadow-brand'

const childClass =
  'ml-[21px] block border-l border-sidebar-border pr-3 pl-5 text-[13.5px] leading-[34px] font-medium text-sidebar-foreground transition-colors hover:text-sidebar-accent-foreground'

const childActive = 'border-sidebar-ring bg-sidebar-accent font-bold text-sidebar-accent-foreground'

/**
 * Navegação do menu lateral: seções com rótulo, itens de 40px com o ativo em
 * brand e grupos recolhíveis cujos subitens se ligam por uma guia vertical.
 */
export function SidebarNav({ items, current, collapsed = false, onNavigate }: SidebarNavProps) {
  const { t } = useTranslation()
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  const containsCurrent = (item: NavItem) => item.children?.some((child) => child.to === current?.to) ?? false
  const isOpen = (item: NavItem) => toggled[item.to] ?? containsCurrent(item)

  return (
    <nav
      aria-label={t('nav.main')}
      className={cn(
        'flex min-h-0 flex-1 [scrollbar-width:thin] [scrollbar-color:var(--sidebar-accent)_transparent] flex-col gap-0.5 overflow-y-auto pt-2 pb-4 *:shrink-0',
        collapsed ? 'px-2' : 'px-3',
      )}
    >
      {items.map((item, index) => {
        const sectionStart = item.group && item.group !== items[index - 1]?.group

        if (collapsed) {
          // Recolhido: o grupo leva à primeira página dele; o nome aparece no tooltip.
          const target = item.children?.[0] ?? item
          const active = item.children ? containsCurrent(item) : undefined

          return (
            <Fragment key={item.to}>
              {sectionStart && index > 0 ? <hr className="mx-2 my-2 border-sidebar-border" /> : null}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Link
                      to={target.to}
                      activeOptions={{ exact: target.exact ?? false }}
                      aria-label={t(item.labelKey)}
                      data-status={active ? 'active' : undefined}
                      className={cn(itemClass, activeClass, 'h-10 justify-center px-0 py-0')}
                    />
                  }
                >
                  <item.icon className="size-[18px]" aria-hidden />
                </TooltipTrigger>
                <TooltipContent side="right">{t(item.labelKey)}</TooltipContent>
              </Tooltip>
            </Fragment>
          )
        }

        return (
          <Fragment key={item.to}>
            {sectionStart ? (
              <p className="px-3 pt-4 pb-1.5 text-[11px] font-bold tracking-[0.1em] text-sidebar-foreground/70 uppercase">
                {t(item.group!)}
              </p>
            ) : null}

            {item.children ? (
              <div>
                <button
                  type="button"
                  aria-expanded={isOpen(item)}
                  onClick={() => setToggled((state) => ({ ...state, [item.to]: !isOpen(item) }))}
                  className={cn(itemClass, isOpen(item) && 'font-bold text-sidebar-accent-foreground')}
                >
                  <item.icon className="size-[18px]" aria-hidden />
                  <span className="flex-1 text-left">{t(item.labelKey)}</span>
                  <ChevronRight
                    className={cn('size-3.5 transition-transform duration-200', isOpen(item) && 'rotate-90')}
                    aria-hidden
                  />
                </button>
                {isOpen(item) ? (
                  <div className="flex animate-in flex-col py-1 duration-200 fade-in-0 slide-in-from-top-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        // Quem decide o ativo é a página atual do menu (a rota mais específica), não o prefixo da URL.
                        activeOptions={{ exact: true }}
                        onClick={onNavigate}
                        className={cn(childClass, current?.to === child.to && childActive)}
                      >
                        {t(child.labelKey)}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                to={item.to}
                activeOptions={{ exact: true }}
                onClick={onNavigate}
                className={cn(itemClass, activeClass, current?.to === item.to && itemActive)}
              >
                <item.icon className="size-[18px]" aria-hidden />
                <span className="flex-1">{t(item.labelKey)}</span>
              </Link>
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
